import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import AnalyticsBootstrap from "@/components/AnalyticsBootstrap";
import TrackingPixels from "@/components/TrackingPixels";
import { HashScrollFix } from "@/components/HashScrollFix";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
// RESONATE_PERF_V3 - Landing is now code-split too (was eager). Its bundle
// (Hero/HeroShowcase/HeroStats/Marquee/StickyBar/...) was shipping to every
// non-homepage route - PageSpeed flagged Hero.jsx/HeroStats.jsx as unused JS
// competing with hydration on /vs/*, /blog/*, /features/* etc. The static
// #hero-shell in index.html already paints the homepage hero independent of
// JS (see RESONATE_HERO_SHELL_V1), so the brief Suspense window before this
// chunk loads is invisible to users - the shell stays up until Landing
// mounts and removes it. scripts/ssg-entry.jsx renders Landing eagerly for
// prerendering regardless (separate Node module graph, unaffected by this).
const Landing = lazy(() => import("@/pages/Landing"));

const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const Author = lazy(() => import("@/pages/Author"));
const Article = lazy(() => import("@/pages/Article"));
const FeatureRouter = lazy(() => import("@/pages/FeatureRouter"));
const FeaturesIndex = lazy(() => import("@/pages/FeaturesIndex"));
const Legal = lazy(() => import("@/pages/Legal"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Book = lazy(() => import("@/pages/Book"));
const Agencies = lazy(() => import("@/pages/Agencies"));
const AgencyFAQ = lazy(() => import("@/pages/AgencyFAQ"));
const Partners = lazy(() => import("@/pages/Partners"));
const DoneForYou = lazy(() => import("@/pages/DoneForYou"));
const About = lazy(() => import("@/pages/About"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const TopicRedirect = lazy(() => import("@/pages/TopicRedirect"));
const SignupPreview = lazy(() => import("@/pages/SignupPreview"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const RouteFallback = () => (
  <div className="min-h-screen grid place-items-center bg-[#FAFAFF]">
    <div className="w-6 h-6 rounded-full border-2 border-[#261F49] border-t-transparent animate-spin" aria-label="Loading" />
  </div>
);

export default function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AnalyticsBootstrap />
        <TrackingPixels />
        <BrowserRouter>
        <ScrollToTop />
        <HashScrollFix />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/author/:slug" element={<Author />} />
            <Route path="/blog/*" element={<Article />} />
            {/* Base44-managed content prefixes.  All render via <Article /> which
                fetches by pathname from getArticles and injects per-page SEO. */}
            <Route path="/vs/*" element={<Article />} />
            <Route path="/compare/*" element={<Article />} />
            <Route path="/features" element={<FeaturesIndex />} />
            <Route path="/features/:slug" element={<FeatureRouter />} />
            <Route path="/features/*" element={<Article />} />
            <Route path="/free/*" element={<Article />} />
            <Route path="/for-personal-brand/*" element={<Article />} />
            <Route path="/for-b2b-marketing/*" element={<Article />} />
            <Route path="/for-agencies/*" element={<Article />} />
            <Route path="/for-founders/*" element={<Article />} />
            <Route path="/best-linkedin-content-tools/*" element={<Article />} />
            <Route path="/linkedin-content-generator" element={<Article />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/security" element={<Legal kind="security" />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/book" element={<Book />} />
            <Route path="/agencies" element={<Agencies />} />
            <Route path="/agencies/faq" element={<AgencyFAQ />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/done-for-you" element={<DoneForYou />} />
            <Route path="/signup-preview" element={<SignupPreview />} />
            {/* Clean topic shortlinks: /mcp, /ideas, /viral, /slack, /ghostwriter, etc. */}
            <Route path="/topics/:topic" element={<TopicRedirect />} />
            <Route path="/mcp" element={<TopicRedirect topic="mcp" />} />
            <Route path="/ideas" element={<TopicRedirect topic="ideas" />} />
            <Route path="/viral" element={<TopicRedirect topic="viral" />} />
            <Route path="/predict" element={<TopicRedirect topic="predict" />} />
            <Route path="/voice" element={<TopicRedirect topic="voice" />} />
            <Route path="/no-time" element={<TopicRedirect topic="no-time" />} />
            <Route path="/ship" element={<TopicRedirect topic="ship" />} />
            <Route path="/build" element={<TopicRedirect topic="build" />} />
            <Route path="/integrations" element={<TopicRedirect topic="integrations" />} />
            <Route path="/ai-chat" element={<TopicRedirect topic="ai-chat" />} />
            <Route path="/slack" element={<TopicRedirect topic="slack" />} />
            <Route path="/ghostwriter" element={<TopicRedirect topic="ghostwriter" />} />
            <Route path="/ghostwriting" element={<TopicRedirect topic="ghostwriting" />} />
            <Route path="/clients" element={<TopicRedirect topic="clients" />} />
            <Route path="/7-minutes" element={<TopicRedirect topic="7-minutes" />} />
            <Route path="/operators" element={<TopicRedirect topic="operators" />} />
            {/* Legacy: /marketplace was renamed to /done-for-you */}
            <Route path="/marketplace" element={<Navigate to="/done-for-you" replace />} />
            {/* Catch-all → 404 (was Navigate to "/" which caused soft-404s in GSC) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}
